/**
 * FE catalog of Mongo `machines` rows from GetSearchData (insight casinos).
 *
 * This is the same mnum → machines lookup that supplies game name / manufacturer
 * (effective manufacturer prefers slotcheckManufacturer when present)
 * for Search. Display ids (machines.slotNumber) are resolved from here — never
 * from casinoInsight / GetCasinoInsights rows.
 */

import {
  getMachineDisplayId,
  type MachineDisplaySource,
} from './machineDisplayId';

export type MachineCatalogRow = {
  /** Present on Mongo machines docs; not used for display lookup. */
  casinoId?: unknown;
  mnum?: unknown;
  slotNumber?: unknown;
  floorLocation?: unknown;
  slot_number?: unknown;
  manufacturer?: unknown;
  game_name?: unknown;
  /** Present on insight GetSearchData machines rows. */
  source?: unknown;
};

let activeByMnum: Map<string, MachineDisplaySource> | null = null;

/**
 * Canonical mnum key — align with ms-casino mnumKey / adaptDoc (NumberInt,
 * "7916.0" → "7916") so catalog lookup survives type changes after rewrites.
 */
const mnumKey = (v: unknown): string => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') {
    const anyV = v as { toNumber?: () => number; valueOf?: () => unknown };
    if (typeof anyV.toNumber === 'function') {
      const n = anyV.toNumber();
      if (Number.isFinite(n)) return String(Math.round(n));
    }
    if (typeof anyV.valueOf === 'function') {
      const n = Number(anyV.valueOf());
      if (Number.isFinite(n)) return String(Math.round(n));
    }
  }
  const s = String(v).trim();
  if (!s) return '';
  if (/^[0-9]+(\.0+)?$/.test(s)) return String(Math.round(Number(s)));
  return s;
};

/** Build mnum → machine fields map from GetSearchData (or equivalent) rows. */
export function buildMachinesByMnum(
  rows: MachineCatalogRow[] | null | undefined
): Map<string, MachineDisplaySource> {
  const map = new Map<string, MachineDisplaySource>();
  for (const row of rows || []) {
    const key = mnumKey(row?.mnum);
    if (!key) continue;
    map.set(key, {
      mnum: key,
      slotNumber: row.slotNumber,
      floorLocation: row.floorLocation,
      slot_number: row.slot_number,
    });
  }
  return map;
}

/**
 * True when cached GetSearchData rows can supply mnum→slotNumber display ids.
 *
 * Legacy Timescale rows have no `mnum` — safe to reuse without slotNumber.
 * Insight machines rows (`mnum` / source:'insight') MUST include at least one
 * usable `slotNumber`; pre-#91 persisted insightSummary cache and any other
 * no-slotNumber catalog would otherwise stick Explore on mnum forever because
 * HotListReport/SearchModal skip the network when casinoId matches.
 */
export function searchDataHasDisplaySlotNumbers(
  rows: MachineCatalogRow[] | null | undefined
): boolean {
  if (!rows || rows.length === 0) return false;
  let sawMnum = false;
  let sawSlotNumber = false;
  for (const row of rows) {
    const key = mnumKey(row?.mnum);
    if (!key) continue;
    sawMnum = true;
    const sn = row.slotNumber;
    if (sn === null || sn === undefined || sn === '') continue;
    if (String(sn).trim() === '') continue;
    sawSlotNumber = true;
    break;
  }
  // No mnum rows → legacy catalog; OK to skip refetch.
  if (!sawMnum) return true;
  return sawSlotNumber;
}

/** Called whenever GetSearchData / SetCasinoSearchData updates the catalog. */
export function setActiveMachinesCatalog(
  rows: MachineCatalogRow[] | null | undefined
): void {
  if (!rows || rows.length === 0) {
    activeByMnum = null;
    return;
  }
  activeByMnum = buildMachinesByMnum(rows);
}

export function clearActiveMachinesCatalog(): void {
  activeByMnum = null;
}

export function getActiveMachinesByMnum(): Map<string, MachineDisplaySource> | null {
  return activeByMnum;
}

/**
 * Display id for an insight EGM: matched machines.slotNumber ?? mnum.
 * `machinesByMnum` defaults to the active GetSearchData catalog.
 */
export function displayIdForInsightMnum(
  mnum: unknown,
  machinesByMnum: Map<string, MachineDisplaySource> | null | undefined = activeByMnum
): string {
  const key = mnumKey(mnum);
  const matched = key && machinesByMnum ? machinesByMnum.get(key) : undefined;
  return getMachineDisplayId({
    slotNumber: matched?.slotNumber,
    mnum: key || mnum,
  });
}

/** Test helper. */
export function __setActiveMachinesByMnumForTests(
  map: Map<string, MachineDisplaySource> | null
): void {
  activeByMnum = map;
}
